import { useEffect, useRef, useState } from 'react';

const plans = [
  {
    name: '랩레슨',
    basePrice: 280000,
    period: '월 4회',
    desc: '플로우부터 라이팅까지, 랩의 모든 것을 배우는 개인 레슨',
    features: ['60분 수업', '개인별 맞춤 커리큘럼', '레슨 녹음 파일 제공', '온라인 피드백'],
    tiers: [
      { label: '기초반', extra: 0 },
      { label: '심화반', extra: 20000 },
      { label: '고급반', extra: 70000 },
    ],
    oneDay: [
      { label: '기초', originalPrice: 70000, price: 50000 },
      { label: '심화', originalPrice: 75000, price: 55000 },
      { label: '고급', originalPrice: 87500, price: 67500 },
    ],
    highlighted: false,
  },
  {
    name: '디제잉',
    basePrice: 280000,
    period: '월 4회',
    desc: '턴테이블부터 클럽 플레이까지, DJ의 기본기를 탄탄하게',
    features: ['60분 수업', '개인별 맞춤 커리큘럼', '레슨 녹음 파일 제공', '공연 기회 연계'],
    tiers: [
      { label: '기초반', extra: 0 },
      { label: '심화반', extra: 20000 },
      { label: '고급반', extra: 70000 },
    ],
    oneDay: [
      { label: '기초', originalPrice: 70000, price: 50000 },
      { label: '심화', originalPrice: 75000, price: 55000 },
      { label: '고급', originalPrice: 87500, price: 67500 },
    ],
    highlighted: false,
  },
  {
    name: '미디작곡',
    basePrice: 280000,
    period: '월 4회',
    desc: 'MIDI 작곡의 기본부터 실전 편곡까지 체계적인 커리큘럼',
    features: ['60분 수업', '개인별 맞춤 커리큘럼', '레슨 녹음 파일 제공', '온라인 피드백'],
    tiers: [
      { label: '기초반', extra: 0 },
      { label: '심화반', extra: 20000 },
      { label: '고급반', extra: 70000 },
    ],
    oneDay: [
      { label: '기초', originalPrice: 70000, price: 50000 },
      { label: '심화', originalPrice: 75000, price: 55000 },
      { label: '고급', originalPrice: 87500, price: 67500 },
    ],
    highlighted: false,
  },
  {
    name: '프로듀싱',
    basePrice: 350000,
    period: '월 4회',
    desc: '비트메이킹부터 믹싱까지, 프로듀서로 성장하는 과정',
    features: ['60분 수업', '장르별 프로듀싱 테크닉', '레슨 녹음 파일 제공', '포트폴리오 제작 지원'],
    tiers: [
      { label: '기초반', extra: 0 },
      { label: '심화반', extra: 20000 },
      { label: '고급반', extra: 50000 },
    ],
    oneDay: [
      { label: '기초', originalPrice: 87500, price: 67500 },
      { label: '심화', originalPrice: 92500, price: 72500 },
      { label: '고급', originalPrice: 100000, price: 80000 },
    ],
    highlighted: false,
  },
];

function formatPrice(price: number) {
  return price.toLocaleString('ko-KR');
}

export default function PricingSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="bg-studio-black py-24 md:py-32 px-6 md:px-12 lg:px-16"
    >
      <div className="w-full">
        {/* Section header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="text-studio-gray/30 font-bebas text-[clamp(60px,10vw,140px)] leading-none block">
            05
          </span>
          <h2
            className="text-white font-bebas uppercase leading-none -mt-6 md:-mt-12"
            style={{ fontSize: 'clamp(40px, 6vw, 80px)', lineHeight: 1 }}
          >
            레슨 요금
          </h2>
          <p className="text-studio-gray font-sans text-lg md:text-xl mt-4">
            투명한 가격, 숨김 없는 정책
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-6 max-w-6xl mx-auto">
          {plans.map((plan, i) => (
            <div
              key={plan.name}
              className={`relative rounded-lg p-6 md:p-7 transition-all duration-500 ${
                plan.highlighted
                  ? 'bg-white text-studio-black border-2 border-white scale-100 md:scale-105 z-10'
                  : 'bg-studio-card border border-studio-border text-white hover:border-studio-muted'
              } ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Plan name */}
              <p className="font-bebas text-xl uppercase tracking-widest mb-1 text-studio-gray">
                {plan.name}
              </p>

              {/* Description */}
              <p className="font-sans text-xs text-studio-silver mb-5 leading-relaxed">
                {plan.desc}
              </p>

              {/* Tiers */}
              <div className="space-y-0">
                {plan.tiers.map((tier, tIdx) => {
                  const price = plan.basePrice + tier.extra;
                  const isLast = tIdx === plan.tiers.length - 1;

                  return (
                    <div key={tier.label}>
                      {/* Tier row */}
                      <div
                        onClick={() => {
                          const el = document.querySelector('#inquiry');
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="flex items-center justify-between py-3 px-3 -mx-3 rounded-md border border-transparent hover:border-studio-silver/40 hover:bg-studio-card/80 cursor-pointer transition-all duration-200"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className={`font-sans text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${
                              tIdx === 0
                                ? 'bg-studio-silver/20 text-studio-silver'
                                : tIdx === 1
                                ? 'bg-studio-gray/30 text-white'
                                : 'bg-white/20 text-white'
                            }`}
                          >
                            {tier.label}
                          </span>
                          <span className="font-bebas text-xl md:text-2xl text-white whitespace-nowrap">
                            ₩{formatPrice(price)}
                          </span>
                          <span className="font-sans text-xs text-studio-silver whitespace-nowrap hidden sm:inline">
                            / {plan.period}
                          </span>
                        </div>
                      </div>

                      {/* Arrow between tiers */}
                      {!isLast && (
                        <div className="flex justify-center py-1">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-down-s-line text-studio-gray text-sm" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* One-Day Class */}
              <div className="mt-4 pt-4 border-t border-studio-border/50">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-sun-line text-studio-gold text-sm" />
                  </div>
                  <span className="font-bebas text-sm text-studio-gold uppercase tracking-wider">
                    원데이 클래스
                  </span>
                  <span className="font-sans text-[10px] text-studio-silver">/ 1회</span>
                </div>
                <div className="space-y-1">
                  {plan.oneDay.map((od) => (
                    <div
                      key={od.label}
                      onClick={() => {
                        const el = document.querySelector('#inquiry');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="flex items-center justify-between py-2 px-2 -mx-2 rounded-md border border-transparent hover:border-studio-gold/30 hover:bg-studio-gold/5 cursor-pointer transition-all duration-200"
                    >
                      <span className="font-sans text-xs text-studio-silver">{od.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bebas text-xs text-studio-gray/40 line-through whitespace-nowrap">
                          ₩{formatPrice(od.originalPrice)}
                        </span>
                        <span className="font-bebas text-sm text-studio-gold whitespace-nowrap">
                          ₩{formatPrice(od.price)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2 mt-5 pt-4 border-t border-studio-border/50">
                {plan.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs text-studio-silver" />
                    </div>
                    <span className="font-sans text-xs text-studio-light">
                      {feat}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Prepay discount notice */}
        <div
          className={`mt-12 text-center transition-all duration-700 delay-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-3 bg-studio-card border border-studio-border rounded-full px-6 py-3">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-price-tag-3-line text-studio-silver text-lg" />
            </div>
            <span className="font-sans text-sm text-studio-silver">
              <strong className="text-white">개인 과정</strong>은 <strong className="text-white">3개월 선결제 시 할인</strong> 혜택이 적용됩니다
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}